import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2, Save, Globe, Lock, ExternalLink, Sparkles, Copy, Plus, Trash2, UserCog,
  FileText, Compass, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { Skeleton } from "@/components/Skeleton";
import PlanUpgradeSection from "@/components/PlanUpgradeSection";
import MarketplaceSettings from "@/components/MarketplaceSettings";

export default function ProfileEditor() {
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profile/me");
        setP(data);
      } catch { toast.error("Could not load profile"); }
    })();
  }, []);

  const set = (patch) => setP((cur) => ({ ...cur, ...patch }));

  const save = async () => {
    if (!p) return;
    setSaving(true);
    try {
      const { data } = await api.put("/profile/me", {
        username: p.username,
        display_name: p.display_name,
        headline: p.headline,
        bio: p.bio,
        photo_url: p.photo_url,
        subjects: p.subjects || [],
        curriculums: p.curriculums || [],
        languages: p.languages || [],
        experience_years: parseInt(p.experience_years || 0, 10),
        teaching_philosophy: p.teaching_philosophy,
        achievements: p.achievements || [],
        certifications: p.certifications || [],
        education: p.education || [],
        experience: p.experience || [],
        is_public: !!p.is_public,
      });
      setP(data);
      toast.success("Profile saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const togglePublic = async () => {
    if (!p) return;
    const next = !p.is_public;
    set({ is_public: next });
    try {
      const { data } = await api.put("/profile/me", { is_public: next });
      setP(data);
      toast.success(next ? "Profile is public" : "Profile is private");
    } catch { toast.error("Could not update visibility"); set({ is_public: !next }); }
  };

  const copyPublicLink = async () => {
    if (!p?.public_url_path) return;
    try { await navigator.clipboard.writeText(`${window.location.origin}${p.public_url_path}`); toast.success("Public link copied"); }
    catch { toast.error("Copy failed"); }
  };

  const addStringTo = (field) => set({ [field]: [...(p[field] || []), ""] });
  const updateStringAt = (field, i, val) => {
    const next = [...(p[field] || [])];
    next[i] = val;
    set({ [field]: next });
  };
  const removeStringAt = (field, i) => {
    const next = [...(p[field] || [])];
    next.splice(i, 1);
    set({ [field]: next });
  };

  if (!p) {
    return (
      <Layout title="Educator profile">
        <Skeleton className="h-32 max-w-3xl" />
      </Layout>
    );
  }

  return (
    <Layout title="Educator profile">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
            <UserCog className="h-3 w-3" /> Educator identity
          </span>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
            Your educator profile
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground max-w-xl">
            Build a premium, public educator identity. Showcase your teaching, lessons and impact.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={togglePublic}
            data-testid="profile-toggle-public"
          >
            {p.is_public ? <><Globe className="h-3.5 w-3.5 mr-1.5" /> Public</> : <><Lock className="h-3.5 w-3.5 mr-1.5" /> Private</>}
          </Button>
          {p.is_public && (
            <>
              <Button variant="outline" className="rounded-full" onClick={copyPublicLink} data-testid="profile-copy-link">
                <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy link
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => window.open(p.public_url_path, "_blank")} data-testid="profile-preview">
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Preview
              </Button>
            </>
          )}
          <Button className="rounded-full" onClick={save} disabled={saving} data-testid="profile-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Save
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 max-w-6xl">
        {/* Basics */}
        <div className="lg:col-span-7 tb-card p-5 space-y-4">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Basics</p>
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-full overflow-hidden border border-border bg-muted grid place-items-center">
              {p.photo_url ? (
                <img src={p.photo_url} alt="" className="h-full w-full object-cover" />
              ) : (
                <UserCog className="h-6 w-6 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <Label>Photo URL</Label>
              <Input value={p.photo_url || ""} onChange={(e) => set({ photo_url: e.target.value })} placeholder="https://…" className="mt-1.5 h-10 rounded-xl" data-testid="profile-photo-url" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Display name</Label>
              <Input value={p.display_name || ""} onChange={(e) => set({ display_name: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="profile-name" />
            </div>
            <div>
              <Label>Username · /u/&lt;username&gt;</Label>
              <Input value={p.username || ""} onChange={(e) => set({ username: e.target.value })} className="mt-1.5 h-11 rounded-xl font-mono" data-testid="profile-username" />
            </div>
          </div>
          <div>
            <Label>Headline</Label>
            <Input value={p.headline || ""} onChange={(e) => set({ headline: e.target.value })} placeholder="e.g. Grade 8 Science teacher · CBSE · Bangalore" className="mt-1.5 h-11 rounded-xl" data-testid="profile-headline" />
          </div>
          <div>
            <Label>About</Label>
            <Textarea rows={4} value={p.bio || ""} onChange={(e) => set({ bio: e.target.value })} placeholder="Brief professional bio" className="mt-1.5 rounded-xl" data-testid="profile-bio" />
          </div>
          <div>
            <Label>Teaching philosophy</Label>
            <Textarea rows={3} value={p.teaching_philosophy || ""} onChange={(e) => set({ teaching_philosophy: e.target.value })} placeholder="What you believe great teaching looks like" className="mt-1.5 rounded-xl" data-testid="profile-philosophy" />
          </div>
        </div>

        {/* Subjects / Languages */}
        <div className="lg:col-span-5 tb-card p-5 space-y-4">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Expertise</p>
          <div>
            <Label>Years of experience</Label>
            <Input type="number" min="0" value={p.experience_years || 0} onChange={(e) => set({ experience_years: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="profile-exp-years" />
          </div>
          <StringListField field="subjects"    label="Subjects"     items={p.subjects}    onAdd={() => addStringTo("subjects")}    onChange={(i, v) => updateStringAt("subjects", i, v)}    onRemove={(i) => removeStringAt("subjects", i)} />
          <StringListField field="curriculums" label="Curriculums"  items={p.curriculums} onAdd={() => addStringTo("curriculums")} onChange={(i, v) => updateStringAt("curriculums", i, v)} onRemove={(i) => removeStringAt("curriculums", i)} />
          <StringListField field="languages"   label="Languages"    items={p.languages}   onAdd={() => addStringTo("languages")}   onChange={(i, v) => updateStringAt("languages", i, v)}   onRemove={(i) => removeStringAt("languages", i)} />
        </div>

        {/* Achievements + Certifications */}
        <div className="lg:col-span-12 tb-card p-5 space-y-4">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Achievements & certifications</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <StringListField field="achievements"   label="Achievements"   items={p.achievements}   onAdd={() => addStringTo("achievements")}   onChange={(i, v) => updateStringAt("achievements", i, v)}   onRemove={(i) => removeStringAt("achievements", i)} placeholder="e.g. Best Teacher Award 2024" />
            <StringListField field="certifications" label="Certifications" items={p.certifications} onAdd={() => addStringTo("certifications")} onChange={(i, v) => updateStringAt("certifications", i, v)} onRemove={(i) => removeStringAt("certifications", i)} placeholder="e.g. NPTEL · Teaching Methodology" />
          </div>
        </div>

        {/* ===== Career Toolkit shortcuts ===== */}
        <div className="lg:col-span-12 tb-card-feature relative overflow-hidden" data-testid="profile-career-toolkit">
          <div className="absolute inset-0 tb-grid-bg opacity-40 pointer-events-none" />
          <div className="relative">
            <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
              <Sparkles className="h-3 w-3" /> Career toolkit
            </span>
            <h2 className="mt-3 font-display text-xl sm:text-2xl tracking-tight font-medium">
              Turn your profile into career capital
            </h2>
            <p className="mt-1.5 text-sm text-muted-foreground max-w-xl">
              Your educator identity above powers a premium AI resume, a tailored career plan, and a shareable public profile link.
            </p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <ToolkitTile
                icon={FileText}
                chip="tb-chip-lavender"
                title="AI Resume Builder"
                hint="6 premium templates · ATS-friendly"
                onClick={() => navigate("/resume")}
                testid="toolkit-resume"
              />
              <ToolkitTile
                icon={Compass}
                chip="tb-chip-sky"
                title="Career Copilot"
                hint="Profile score · interview prep · cert advice"
                onClick={() => navigate("/career")}
                testid="toolkit-career"
              />
              <ToolkitTile
                icon={Globe}
                chip="tb-chip-mint"
                title={p.is_public ? "Your public profile" : "Publish your profile"}
                hint={p.is_public ? "Live · share with parents & schools" : "Make it discoverable in one tap"}
                onClick={() => p.is_public ? window.open(p.public_url_path, "_blank") : togglePublic()}
                testid="toolkit-publish"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ===== Plan upgrade section ===== */}
      <PlanUpgradeSection source="profile" id="plans" />

      {/* ===== Educator Marketplace settings ===== */}
      <div className="mt-8">
        <MarketplaceSettings />
      </div>
    </Layout>
  );
}

function ToolkitTile({ icon: Icon, chip, title, hint, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="tb-card-interactive p-4 text-left group"
      data-testid={testid}
    >
      <span className={`inline-grid place-items-center h-9 w-9 rounded-xl ${chip}`}>
        <Icon className="h-4 w-4" />
      </span>
      <p className="mt-3 text-sm font-display font-medium tracking-tight leading-snug">{title}</p>
      <p className="mt-1 text-[11px] text-muted-foreground line-clamp-2">{hint}</p>
      <span className="mt-2 inline-flex items-center gap-1 text-[11px] text-primary">
        Open <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
      </span>
    </button>
  );
}

function StringListField({ field, label, items = [], onAdd, onChange, onRemove, placeholder }) {
  // Stable ids per slot — keeps input DOM stable across reorder/delete so user-typed
  // text doesn't end up displayed against the wrong array slot.
  const idsRef = useRef([]);
  const ids = useMemo(() => {
    const cur = idsRef.current;
    if (cur.length < items.length) {
      while (cur.length < items.length) {
        cur.push(`${field}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
      }
    } else if (cur.length > items.length) {
      cur.length = items.length;
    }
    return [...cur];
    // We intentionally re-run on length change only; content edits keep the same id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, field]);

  return (
    <div data-testid={`profile-list-${field}`}>
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <button onClick={onAdd} className="text-[11px] text-primary hover:underline inline-flex items-center gap-1" data-testid={`profile-add-${field}`}>
          <Plus className="h-3 w-3" /> Add
        </button>
      </div>
      <div className="mt-1.5 space-y-2">
        {items.length === 0 && <p className="text-[11px] text-muted-foreground italic">None yet</p>}
        {items.map((item, i) => (
          <div key={ids[i] || `${field}-${i}`} className="flex gap-2">
            <Input value={item} onChange={(e) => onChange(i, e.target.value)} placeholder={placeholder} className="h-9 rounded-lg" />
            <Button variant="ghost" size="icon" onClick={() => { idsRef.current.splice(i, 1); onRemove(i); }} className="h-9 w-9 text-muted-foreground hover:text-destructive">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
